import React from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  ShieldCheck,
  ShieldOff,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/lib/auth-oidc'
import {
  useDeleteQuota,
  useExcludeFromQuota,
  useIncludeInQuota,
  useMyQuota,
  useQuotas,
  useResetQuotaUsage,
  useSetQuotaLimit,
} from '@/lib/api'
import { localizeDigits } from '@/lib/localize-digits'

export const Route = createFileRoute('/settings/quotas')({
  component: QuotasPage,
})

type QuotaStatus = 'OK' | 'EXCEEDED' | 'EXCLUDED' | string

function statusBadgeVariant(
  status: QuotaStatus,
): 'default' | 'destructive' | 'secondary' | 'outline' {
  if (status === 'EXCEEDED') return 'destructive'
  if (status === 'EXCLUDED') return 'outline'
  return 'secondary'
}

function UsageBar({
  used,
  limit,
  status,
}: {
  used: number
  limit: number
  status: QuotaStatus
}) {
  const { t } = useTranslation()
  const remaining = Math.max(0, limit - used)
  const usagePercent = limit > 0 ? Math.min(100, (used / limit) * 100) : 0
  const isExceeded = status === 'EXCEEDED'
  const isExcluded = status === 'EXCLUDED'

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>
          {t('quotas.usedToday')}:{' '}
          <span className="font-medium text-foreground">
            {localizeDigits(String(used))}
          </span>
        </span>
        <span>
          {t('quotas.dailyLimit')}:{' '}
          <span className="font-medium text-foreground">
            {isExcluded ? '∞' : localizeDigits(String(limit))}
          </span>
        </span>
      </div>
      {!isExcluded && (
        <div className="h-2 w-full rounded-full bg-secondary">
          <div
            className={`h-2 rounded-full transition-all ${
              isExceeded
                ? 'bg-destructive'
                : usagePercent > 80
                  ? 'bg-orange-500'
                  : 'bg-primary'
            }`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {isExcluded
          ? t('quotas.statusExcluded')
          : `${t('quotas.remaining')}: ${localizeDigits(String(remaining))}`}
      </p>
    </div>
  )
}

function PersonalQuotaView() {
  const { t } = useTranslation()
  const { data: myQuota, isLoading } = useMyQuota()

  return (
    <div className="space-y-4 pt-4">
      {isLoading ? (
        <LoadingSpinner label={t('quotas.loading')} />
      ) : !myQuota ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>{t('quotas.noQuotaInfo')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('quotas.myQuota')}</CardTitle>
              <Badge variant={statusBadgeVariant(myQuota.status)}>
                {myQuota.status === 'OK'
                  ? t('quotas.statusOk')
                  : myQuota.status === 'EXCLUDED'
                    ? t('quotas.statusExcluded')
                    : t('quotas.statusExceeded')}
              </Badge>
            </div>
            <CardDescription>{t('quotas.myQuotaDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <UsageBar
              used={myQuota.used_today}
              limit={myQuota.daily_limit}
              status={myQuota.status}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function QuotasPage() {
  const { t } = useTranslation()
  const { user, isLoading: authLoading } = useAuth()
  const { data: quotasData, isLoading: quotasLoading } = useQuotas()
  const setLimitMutation = useSetQuotaLimit()
  const resetMutation = useResetQuotaUsage()
  const deleteMutation = useDeleteQuota()
  const excludeMutation = useExcludeFromQuota()
  const includeMutation = useIncludeInQuota()

  const [editingAccount, setEditingAccount] = React.useState<string | null>(
    null,
  )
  const [editLimit, setEditLimit] = React.useState('')
  const [resetAccount, setResetAccount] = React.useState<string | null>(null)
  const [deleteAccount, setDeleteAccount] = React.useState<string | null>(null)
  const [excludeAccount, setExcludeAccount] = React.useState<string | null>(
    null,
  )
  const [includeAccount, setIncludeAccount] = React.useState<string | null>(
    null,
  )

  const isAdmin = user?.is_admin ?? false
  const isLoading = authLoading || quotasLoading

  const handleEditSave = () => {
    if (!editingAccount) return
    const limit = parseInt(editLimit, 10)
    if (isNaN(limit) || limit <= 0 || limit > 10000) return

    setLimitMutation.mutate(
      { accountName: editingAccount, dailyLimit: limit },
      {
        onSuccess: () => {
          toast.success(t('quotas.limitUpdated'))
          setEditingAccount(null)
        },
        onError: () => {
          toast.error(t('quotas.error'))
        },
      },
    )
  }

  const handleReset = () => {
    if (!resetAccount) return
    resetMutation.mutate(resetAccount, {
      onSuccess: () => {
        toast.success(t('quotas.usageReset'))
        setResetAccount(null)
      },
      onError: () => {
        toast.error(t('quotas.error'))
      },
    })
  }

  const handleDelete = () => {
    if (!deleteAccount) return
    deleteMutation.mutate(deleteAccount, {
      onSuccess: () => {
        toast.success(t('quotas.quotaDeleted'))
        setDeleteAccount(null)
      },
      onError: () => {
        toast.error(t('quotas.error'))
      },
    })
  }

  const handleExclude = () => {
    if (!excludeAccount) return
    excludeMutation.mutate(excludeAccount, {
      onSuccess: () => {
        toast.success(t('quotas.excludeSuccess'))
        setExcludeAccount(null)
      },
      onError: () => {
        toast.error(t('quotas.error'))
      },
    })
  }

  const handleInclude = () => {
    if (!includeAccount) return
    includeMutation.mutate(includeAccount, {
      onSuccess: () => {
        toast.success(t('quotas.includeSuccess'))
        setIncludeAccount(null)
      },
      onError: () => {
        toast.error(t('quotas.error'))
      },
    })
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/settings">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">{t('quotas.title')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('quotas.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        {/* Non-admin: show personal quota */}
        {!authLoading && !isAdmin && <PersonalQuotaView />}

        {/* Admin: show all quotas */}
        {isAdmin && (
          <div className="space-y-4 pt-4">
            {isLoading ? (
              <LoadingSpinner label={t('quotas.loading')} />
            ) : !quotasData?.quotas.length ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <p className="font-medium">{t('quotas.noQuotas')}</p>
                  <p className="text-xs mt-1">
                    {t('quotas.noQuotasDescription')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              quotasData.quotas.map((quota) => {
                const isExcluded = quota.excluded

                return (
                  <Card key={quota.account_name}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <CardTitle className="text-base truncate">
                            {quota.account_name}
                          </CardTitle>
                          <Badge
                            variant={statusBadgeVariant(
                              quota.status ?? (isExcluded ? 'EXCLUDED' : 'OK'),
                            )}
                            className="shrink-0"
                          >
                            {isExcluded
                              ? t('quotas.statusExcluded')
                              : quota.used_today >= quota.daily_limit
                                ? t('quotas.statusExceeded')
                                : t('quotas.statusOk')}
                          </Badge>
                        </div>

                        {/* Actions dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 shrink-0"
                              aria-label={t('quotas.actions')}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingAccount(quota.account_name)
                                setEditLimit(String(quota.daily_limit))
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                              {t('quotas.editLimit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                setResetAccount(quota.account_name)
                              }
                            >
                              <RotateCcw className="h-4 w-4" />
                              {t('quotas.resetUsage')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {isExcluded ? (
                              <DropdownMenuItem
                                onClick={() =>
                                  setIncludeAccount(quota.account_name)
                                }
                              >
                                <ShieldCheck className="h-4 w-4" />
                                {t('quotas.include')}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() =>
                                  setExcludeAccount(quota.account_name)
                                }
                              >
                                <ShieldOff className="h-4 w-4" />
                                {t('quotas.exclude')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() =>
                                setDeleteAccount(quota.account_name)
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                              {t('quotas.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <CardDescription>
                        {t('quotas.lastReset')}:{' '}
                        {localizeDigits(
                          new Date(quota.last_reset_date).toLocaleDateString(),
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <UsageBar
                        used={quota.used_today}
                        limit={quota.daily_limit}
                        status={
                          isExcluded
                            ? 'EXCLUDED'
                            : quota.used_today >= quota.daily_limit
                              ? 'EXCEEDED'
                              : 'OK'
                        }
                      />
                    </CardContent>
                  </Card>
                )
              })
            )}
          </div>
        )}

        {/* Fallback for auth-loading state */}
        {authLoading && (
          <div className="pt-4">
            <LoadingSpinner label={t('quotas.loading')} />
          </div>
        )}
      </div>

      {/* Edit Limit Dialog */}
      <Dialog
        open={!!editingAccount}
        onOpenChange={(open) => !open && setEditingAccount(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('quotas.editLimitTitle')}</DialogTitle>
            <DialogDescription>
              {t('quotas.editLimitDescription', {
                account: editingAccount,
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="number"
              min={1}
              max={10000}
              value={editLimit}
              onChange={(e) => setEditLimit(e.target.value)}
              placeholder={t('quotas.newLimit')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAccount(null)}>
              {t('quotas.cancel')}
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={setLimitMutation.isPending}
            >
              {t('quotas.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog
        open={!!resetAccount}
        onOpenChange={(open) => !open && setResetAccount(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('quotas.confirmReset')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('quotas.confirmResetDescription', {
                account: resetAccount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quotas.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={resetMutation.isPending}
            >
              {t('quotas.resetUsage')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteAccount}
        onOpenChange={(open) => !open && setDeleteAccount(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('quotas.confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('quotas.confirmDeleteDescription', {
                account: deleteAccount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quotas.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {t('quotas.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Exclude Confirmation Dialog */}
      <AlertDialog
        open={!!excludeAccount}
        onOpenChange={(open) => !open && setExcludeAccount(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <ShieldOff className="h-5 w-5 inline me-2 text-muted-foreground" />
              {t('quotas.confirmExclude')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('quotas.confirmExcludeDescription', {
                account: excludeAccount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quotas.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExclude}
              disabled={excludeMutation.isPending}
            >
              {t('quotas.exclude')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Include Confirmation Dialog */}
      <AlertDialog
        open={!!includeAccount}
        onOpenChange={(open) => !open && setIncludeAccount(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <ShieldCheck className="h-5 w-5 inline me-2 text-muted-foreground" />
              {t('quotas.confirmInclude')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('quotas.confirmIncludeDescription', {
                account: includeAccount,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quotas.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleInclude}
              disabled={includeMutation.isPending}
            >
              {t('quotas.include')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
